import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { supabase } from '@/lib/supabase'
import { hasUnsavedMetricsChanges } from '@/lib/unsavedChanges'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatPartnerDisplay(
  partnerNumber: number,
  personsFio?: string | null,
  citiesText?: string | null
): string {
  const parts = [personsFio, citiesText].filter(s => s && s.trim().length > 0) as string[]
  const head = parts.length > 0 ? parts.join(', ') : 'Франчайзи'
  return `${head} (${partnerNumber})`
}

function isValidIso4217CurrencyCode(currency: unknown): currency is string {
  // Intl.NumberFormat expects a valid ISO 4217 code like "RUB", "USD".
  return typeof currency === 'string' && /^[A-Z]{3}$/.test(currency.trim())
}

export function formatCurrency(amount: number, currency: string = 'RUB'): string {
  const normalized = currency?.trim()
  if (!isValidIso4217CurrencyCode(normalized)) {
    // Never crash the UI due to bad data; show a plain number instead.
    return formatNumberRounded(amount)
  }

  try {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: normalized,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  } catch {
    // Some runtimes can still throw even with a 3-letter code (custom/unknown).
    return formatNumberRounded(amount)
  }
}

export function formatCurrencyWithOptionalKopecks(amount: number, currency: string = 'RUB'): string {
  const normalized = currency?.trim()
  if (!isValidIso4217CurrencyCode(normalized)) {
    return formatNumber(amount)
  }

  const hasFraction = Math.abs(amount - Math.round(amount)) > 0.004
  try {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: normalized,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0,
    }).format(amount)
  } catch {
    return formatNumber(amount)
  }
}

export function getCurrencySymbol(currency: string = 'RUB'): string {
  const normalized = currency?.trim()
  if (!isValidIso4217CurrencyCode(normalized)) return currency

  try {
    return (
      new Intl.NumberFormat('ru-RU', { style: 'currency', currency: normalized })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? normalized
    )
  } catch {
    return normalized
  }
}

export function formatNumber(num: number): string {
  return new Intl.NumberFormat('ru-RU').format(num)
}

export function formatNumberRounded(num: number): string {
  return new Intl.NumberFormat('ru-RU').format(Math.round(num))
}

export function formatPercentage(value: number): string {
  return `${Math.round(value)}%`
}

export function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.charAt(0)?.toUpperCase() || ''
  const last = lastName?.charAt(0)?.toUpperCase() || ''
  return first + last || '?'
}

export function isCurrentMonth(date: string): boolean {
  const now = new Date()
  const targetDate = new Date(date)
  return (
    now.getFullYear() === targetDate.getFullYear() &&
    now.getMonth() === targetDate.getMonth()
  )
}

export function getMonthName(date: string): string {
  return new Intl.DateTimeFormat('ru-RU', { 
    month: 'long', 
    year: 'numeric' 
  }).format(new Date(date))
}

export function getCurrentMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

// Утилита для API запросов с JWT токеном и автообновлением
export async function apiRequest(url: string, options: RequestInit = {}) {
  const getSessionToken = async (): Promise<string | null> => {
    try {
      const { data } = await supabase.auth.getSession()
      return data.session?.access_token ?? null
    } catch (err) {
      console.warn('⚠️ Failed to get Supabase session token:', err)
      return null
    }
  }

  let token = await getSessionToken()
  if (!token) {
    token = localStorage.getItem('auth_token')
  }
  
  const makeRequest = async (authToken: string | null) => {
    const headers = new Headers(options.headers)
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    if (authToken) {
      headers.set('Authorization', `Bearer ${authToken}`)
    }

    return fetch(url, {
      ...options,
      headers,
    })
  }

  let response = await makeRequest(token)

  // Если токен истек, пытаемся обновить его
  if (response.status === 401) {
    console.log('🔄 Access token expired, attempting Supabase session refresh...')
    
    try {
      const { data, error } = await supabase.auth.refreshSession()
      if (error) {
        throw error
      }

      const refreshedToken = data.session?.access_token ?? null
      if (refreshedToken) {
        localStorage.setItem('auth_token', refreshedToken)
        console.log('✅ Supabase session refreshed successfully')
        response = await makeRequest(refreshedToken)
      }
    } catch (refreshError) {
      console.error('❌ Failed to refresh Supabase session:', refreshError)
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || errorData.message || `API request failed: ${response.status} ${response.statusText}`)
  }

  // Проверяем, есть ли контент для парсинга
  const contentType = response.headers.get('content-type')
  const contentLength = response.headers.get('content-length')
  
  // Если нет контента или контент пустой, возвращаем null
  if (contentLength === '0' || !contentType?.includes('application/json')) {
    return null
  }
  
  // Пытаемся парсить JSON, если есть контент
  const text = await response.text()
  if (!text.trim()) {
    return null
  }
  
  try {
    return JSON.parse(text)
  } catch (parseError) {
    console.warn('⚠️ Failed to parse JSON response:', parseError)
    return null
  }
}

export function getPreviousMonth(month: string): string {
  const [year, monthNum] = month.split('-').map(Number)
  const date = new Date(year, monthNum - 1, 1)
  date.setMonth(date.getMonth() - 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

export function getNextMonth(month: string): string {
  const [year, monthNum] = month.split('-').map(Number)
  const date = new Date(year, monthNum - 1, 1)
  date.setMonth(date.getMonth() + 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Универсальная функция для очистки всего кэша приложения
 * Очищает localStorage, sessionStorage и перезагружает страницу
 */
export type AppRefreshStep =
  | 'start'
  | 'local_storage'
  | 'session_storage'
  | 'service_workers'
  | 'cache_storage'
  | 'done'
  | 'reload'
  | 'error'

export interface AppRefreshProgress {
  step: AppRefreshStep
  message: string
}

type AppRefreshProgressCallback = (progress: AppRefreshProgress) => void

interface ClearAppCacheOptions {
  preserveAuthToken?: boolean
  preserveAuthStore?: boolean  // сохраняет Zustand auth-storage и studio-storage
  appVersion?: string
  onProgress?: AppRefreshProgressCallback
  reload?: boolean
}

interface RemoteVersionMetadata {
  appVersion?: string
  deployId?: string
  gitSha?: string
  build?: string
}

const REMOTE_VERSION_CHECK_INTERVAL_MS = 60 * 60 * 1000
const REMOTE_VERSION_LAST_CHECK_KEY = 'remote_version_last_check_at'
const REMOTE_VERSION_RELOAD_GUARD_KEY = 'remote_version_reload_target'

const REMOTE_VERSION_EDITING_PATH_PATTERNS = [
  /^\/partners\/[^/]+\/studios\/[^/]+\/metrics(?:\/|$)/,
  /^\/partners\/[^/]+\/studios\/[^/]+\/studio-metrics(?:\/|$)/,
  /^\/partners\/[^/]+\/studios\/[^/]+\/studio-metrics-grid(?:\/|$)/,
  /^\/partners\/[^/]+\/marketing\/plan(?:\/|$)/,
  /^\/partners\/[^/]+\/studios\/[^/]+\/marketing\/plan(?:\/|$)/,
  /^\/royalties\/(?:import|allocation|process|contract\/)/,
  /^\/settings\/notifications(?:\/|$)/,
  /^\/admin\/notifications(?:\/|$)/,
  // Форма отсмотра — 8–15 минут ввода, автоперезагрузка версии её уничтожит.
  // Адрес переехал с /management/trainer-checklist на /management/pedagogy/reviews/new;
  // старый оставлен, чтобы вкладка, открытая до выката, тоже не перезагружалась.
  /^\/management\/pedagogy\/reviews\/new(?:\/|$)/,
  /^\/management\/(?:trainer-checklist|studio-closures|contract-runbook-safety)(?:\/|$)/,
]

function reportRefreshProgress(
  onProgress: AppRefreshProgressCallback | undefined,
  step: AppRefreshStep,
  message: string
): void {
  onProgress?.({ step, message })
}

async function clearServiceWorkersAndCaches(onProgress?: AppRefreshProgressCallback): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map((registration) => registration.unregister()))
    const message = `Service Worker отключен (${registrations.length})`
    console.log(`✅ ${message}`)
    reportRefreshProgress(onProgress, 'service_workers', message)
  } else {
    reportRefreshProgress(onProgress, 'service_workers', 'Service Worker не используется')
  }

  if ('caches' in window) {
    const cacheKeys = await caches.keys()
    await Promise.all(cacheKeys.map((key) => caches.delete(key)))
    const message = `Cache Storage очищен (${cacheKeys.length})`
    console.log(`✅ ${message}`)
    reportRefreshProgress(onProgress, 'cache_storage', message)
  } else {
    reportRefreshProgress(onProgress, 'cache_storage', 'Cache Storage не поддерживается')
  }
}

export async function ensureFreshDevClient(): Promise<void> {
  if (!import.meta.env.DEV) {
    return
  }

  try {
    await clearServiceWorkersAndCaches()
  } catch (error) {
    console.warn('⚠️ Не удалось очистить dev cache/service workers:', error)
  }
}

function forceReloadWithCacheBust(): void {
  const nextUrl = new URL(window.location.href)
  nextUrl.searchParams.set('cache_bust', Date.now().toString())
  window.location.replace(nextUrl.toString())
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchRemoteVersionMetadata(): Promise<RemoteVersionMetadata | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    })

    if (!response.ok) {
      return null
    }

    const text = await response.text()
    if (!text.trim()) return null

    try {
      return JSON.parse(text) as RemoteVersionMetadata
    } catch {
      console.warn('⚠️ version.json returned non-JSON payload')
      return null
    }
  } catch (error) {
    console.warn('⚠️ Не удалось получить version.json:', error)
    return null
  }
}

function resolveRemoteVersion(metadata: RemoteVersionMetadata | null): string | null {
  if (!metadata) return null
  if (metadata.appVersion) return metadata.appVersion
  if (metadata.gitSha) return metadata.gitSha.slice(0, 7)
  if (metadata.deployId) return metadata.deployId
  return null
}

function resolveCurrentAppVersion(): string | null {
  return import.meta.env.VITE_APP_VERSION || null
}

function isEditingRoute(pathname: string): boolean {
  return REMOTE_VERSION_EDITING_PATH_PATTERNS.some((pattern) => pattern.test(pathname))
}

function isEditableElementActive(): boolean {
  const activeElement = document.activeElement
  if (!activeElement || !(activeElement instanceof HTMLElement)) return false

  const tagName = activeElement.tagName.toLowerCase()
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    activeElement.isContentEditable
  )
}

function shouldSkipRemoteVersionCheck(): boolean {
  if (import.meta.env.DEV) return true
  if (document.visibilityState === 'hidden') return true
  if (hasUnsavedMetricsChanges()) return true
  if (isEditingRoute(window.location.pathname)) return true
  if (isEditableElementActive()) return true
  return false
}

function wasRemoteVersionCheckedRecently(now: number): boolean {
  const lastCheckAt = Number(localStorage.getItem(REMOTE_VERSION_LAST_CHECK_KEY) || 0)
  return lastCheckAt > 0 && now - lastCheckAt < REMOTE_VERSION_CHECK_INTERVAL_MS
}

function rememberRemoteVersionCheck(now: number): void {
  localStorage.setItem(REMOTE_VERSION_LAST_CHECK_KEY, String(now))
}

function forceVersionReload(version: string): void {
  const nextUrl = new URL(window.location.href)
  nextUrl.searchParams.set('v', version)
  nextUrl.searchParams.set('cache_bust', Date.now().toString())
  window.location.replace(nextUrl.toString())
}

async function runRemoteVersionCheck(): Promise<void> {
  if (shouldSkipRemoteVersionCheck()) {
    return
  }

  const currentVersion = resolveCurrentAppVersion()
  if (!currentVersion) {
    return
  }

  const now = Date.now()
  if (wasRemoteVersionCheckedRecently(now)) {
    return
  }
  rememberRemoteVersionCheck(now)

  const remoteMetadata = await fetchRemoteVersionMetadata()
  const remoteVersion = resolveRemoteVersion(remoteMetadata)

  if (!remoteVersion || remoteVersion === currentVersion) {
    localStorage.removeItem(REMOTE_VERSION_RELOAD_GUARD_KEY)
    return
  }

  const alreadyReloadedFor = localStorage.getItem(REMOTE_VERSION_RELOAD_GUARD_KEY)
  if (alreadyReloadedFor === remoteVersion) {
    console.warn('⚠️ Обновление уже запрашивалось для версии', remoteVersion)
    return
  }

  localStorage.setItem(REMOTE_VERSION_RELOAD_GUARD_KEY, remoteVersion)
  console.log('🆕 Обнаружена новая версия на сервере:', { currentVersion, remoteVersion })

  await clearServiceWorkersAndCaches().catch((error) => {
    console.warn('⚠️ Не удалось очистить Service Worker/cache перед обновлением:', error)
  })
  forceVersionReload(remoteVersion)
}

export function checkForRemoteVersionUpdate(): void {
  const scheduleCheck = () => {
    void runRemoteVersionCheck().catch((error) => {
      console.warn('⚠️ Проверка удаленной версии завершилась ошибкой:', error)
    })
  }

  scheduleCheck()
  window.setInterval(scheduleCheck, REMOTE_VERSION_CHECK_INTERVAL_MS)

  window.addEventListener('online', scheduleCheck)
  window.addEventListener('pageshow', scheduleCheck)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      scheduleCheck()
    }
  })
}

async function resetClientCache(options?: ClearAppCacheOptions): Promise<void> {
  const STORAGE_KEY = 'app_version'
  const LAST_CACHE_CLEAR_KEY = 'last_cache_clear'
  const authToken = options?.preserveAuthToken ? localStorage.getItem('auth_token') : null
  const authStore = options?.preserveAuthStore ? localStorage.getItem('auth-storage') : null
  const studioStore = options?.preserveAuthStore ? localStorage.getItem('studio-storage') : null

  localStorage.clear()
  reportRefreshProgress(options?.onProgress, 'local_storage', 'Local Storage очищен')
  sessionStorage.clear()
  reportRefreshProgress(options?.onProgress, 'session_storage', 'Session Storage очищен')

  if (authToken) {
    localStorage.setItem('auth_token', authToken)
  }
  if (authStore) {
    localStorage.setItem('auth-storage', authStore)
  }
  if (studioStore) {
    localStorage.setItem('studio-storage', studioStore)
  }
  if (options?.appVersion) {
    localStorage.setItem(STORAGE_KEY, options.appVersion)
  }
  localStorage.setItem(LAST_CACHE_CLEAR_KEY, new Date().toISOString())

  await clearServiceWorkersAndCaches(options?.onProgress)
  reportRefreshProgress(options?.onProgress, 'done', 'Очистка завершена')
}

export async function clearAppCacheWithProgress(options?: ClearAppCacheOptions): Promise<void> {
  const shouldReload = options?.reload !== false
  reportRefreshProgress(options?.onProgress, 'start', 'Запуск принудительного обновления...')

  try {
    await resetClientCache(options)
    if (shouldReload) {
      reportRefreshProgress(options?.onProgress, 'reload', 'Перезагрузка приложения...')
      await sleep(250)
      forceReloadWithCacheBust()
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Неизвестная ошибка'
    reportRefreshProgress(options?.onProgress, 'error', `Ошибка очистки: ${message}`)
    if (shouldReload) {
      forceReloadWithCacheBust()
    }
    throw error
  }
}

export function clearAppCache(): void {
  try {
    console.log('🧹 Очистка кэша приложения...')
    void clearAppCacheWithProgress().then(() => {
      console.log('✅ Кэш очищен')
    }).catch((error) => {
      console.error('❌ Ошибка при очистке кэша:', error)
    })
  } catch (error) {
    console.error('❌ Ошибка при очистке кэша:', error)
    forceReloadWithCacheBust()
  }
}

export function disableLegacyServiceWorkers(): void {
  if (!('serviceWorker' in navigator)) {
    return
  }

  void navigator.serviceWorker.getRegistrations().then(async (registrations) => {
    if (!registrations.length) {
      return
    }

    await Promise.all(registrations.map((registration) => registration.unregister()))
    console.log(`✅ Legacy Service Worker отключен (${registrations.length})`)

    if ('caches' in window) {
      const cacheKeys = await caches.keys()
      const legacyKeys = cacheKeys.filter((key) => key.includes('workbox') || key.includes('precache'))
      await Promise.all(legacyKeys.map((key) => caches.delete(key)))
    }
  }).catch((error) => {
    console.warn('⚠️ Не удалось отключить legacy Service Worker:', error)
  })
}

/**
 * Проверяет версию приложения и автоматически очищает кэш при обновлении
 * Вызывайте эту функцию при инициализации приложения
 */
export function checkAndClearCacheOnUpdate(): void {
  try {
    // Используем переменную окружения или дату сборки как версию
    const APP_VERSION = import.meta.env.VITE_APP_VERSION || 
                        import.meta.env.VITE_BUILD_DATE || 
                        '1.0.0'
    const STORAGE_KEY = 'app_version'
    const LAST_CACHE_CLEAR_KEY = 'last_cache_clear'
    const CACHE_CLEAR_INTERVAL_DAYS = 7 // Очищать кэш каждые 7 дней
    
    const storedVersion = localStorage.getItem(STORAGE_KEY)
    const lastCacheClear = localStorage.getItem(LAST_CACHE_CLEAR_KEY)
    
    console.log('🔍 Проверка версии приложения:', {
      currentVersion: APP_VERSION,
      storedVersion,
      lastCacheClear
    })
    
    // Проверяем, изменилась ли версия
    if (storedVersion && storedVersion !== APP_VERSION) {
      console.log('🆕 Обнаружена новая версия приложения:', {
        old: storedVersion,
        new: APP_VERSION
      })
      console.log('🧹 Автоматическая очистка кэша при обновлении...')
      
      void clearAppCacheWithProgress({ preserveAuthToken: true, appVersion: APP_VERSION }).then(() => {
        console.log('✅ Кэш очищен, версия обновлена')
      }).catch((error) => {
        console.error('❌ Ошибка при очистке кэша:', error)
      })
      return
    }
    
    // Проверяем периодическую очистку кэша
    if (lastCacheClear) {
      const lastClearDate = new Date(lastCacheClear)
      const daysSinceLastClear = Math.floor(
        (Date.now() - lastClearDate.getTime()) / (1000 * 60 * 60 * 24)
      )
      
      if (daysSinceLastClear >= CACHE_CLEAR_INTERVAL_DAYS) {
        console.log(`🗓️ Прошло ${daysSinceLastClear} дней с последней очистки кэша`)
        console.log('🧹 Автоматическая периодическая очистка кэша...')
        
        void clearAppCacheWithProgress({ preserveAuthToken: true, appVersion: APP_VERSION }).then(() => {
          console.log('✅ Периодическая очистка кэша выполнена')
        }).catch((error) => {
          console.error('❌ Ошибка при периодической очистке кэша:', error)
        })
        return
      }
    }
    
    // Если версия не сохранена, сохраняем текущую
    if (!storedVersion) {
      console.log('📝 Сохранение текущей версии приложения:', APP_VERSION)
      localStorage.setItem(STORAGE_KEY, APP_VERSION)
      localStorage.setItem(LAST_CACHE_CLEAR_KEY, new Date().toISOString())
    }
    
  } catch (error) {
    console.error('❌ Ошибка при проверке версии:', error)
  }
}

/**
 * Универсальная функция для склонения окончаний 
 */
export function pluralizeRu(
  value: number,
  one: string,
  few: string,
  many: string
) {
  const abs = Math.abs(value) % 100
  const last = abs % 10

  if (abs > 10 && abs < 20) return many
  if (last > 1 && last < 5) return few
  if (last === 1) return one
  return many
}

// ============================================================
// Функции для работы с "фиктивными" месяцами (metric_periods)
// ============================================================

export interface MetricPeriod {
  period_id: number
  period_start: string
  period_end: string
  period_len_days: number
  month_number: number
  year_number: number
  week_start: number
  week_end: number
}

/**
 * Получить текущий период из metric_periods
 */
export async function getCurrentPeriod(supabase: any): Promise<MetricPeriod | null> {
  try {
    const d = new Date()
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

    const { data, error } = await supabase
      .from('metric_periods')
      .select('*')
      .lte('period_start', today)
      .gte('period_end', today)
      .maybeSingle()
    
    if (error || !data) {
      if (error) console.error('Error fetching current period:', error)
      return null
    }
    
    return data
  } catch (err) {
    console.error('Error in getCurrentPeriod:', err)
    return null
  }
}

/**
 * Получить предыдущий период
 */
export async function getPreviousPeriod(
  supabase: any, 
  currentPeriod: { year_number: number, month_number: number }
): Promise<MetricPeriod | null> {
  try {
    // Сначала пытаемся найти предыдущий месяц в том же году
    const { data, error } = await supabase
      .from('metric_periods')
      .select('*')
      .eq('year_number', currentPeriod.year_number)
      .eq('month_number', currentPeriod.month_number - 1)
      .maybeSingle()
    
    if (!error && data) {
      return data
    }
    
    // Если это январь, берем декабрь предыдущего года
    const { data: prevYearData, error: prevYearError } = await supabase
      .from('metric_periods')
      .select('*')
      .eq('year_number', currentPeriod.year_number - 1)
      .eq('month_number', 12)
      .maybeSingle()
    
    if (prevYearError) {
      console.error('Error fetching previous period:', prevYearError)
      return null
    }
    
    return prevYearData
  } catch (err) {
    console.error('Error in getPreviousPeriod:', err)
    return null
  }
}

/**
 * Получить следующий период
 */
export async function getNextPeriod(
  supabase: any, 
  currentPeriod: { year_number: number, month_number: number }
): Promise<MetricPeriod | null> {
  try {
    // Сначала пытаемся найти следующий месяц в том же году
    const { data, error } = await supabase
      .from('metric_periods')
      .select('*')
      .eq('year_number', currentPeriod.year_number)
      .eq('month_number', currentPeriod.month_number + 1)
      .maybeSingle()
    
    if (!error && data) {
      return data
    }
    
    // Если это декабрь, берем январь следующего года
    const { data: nextYearData, error: nextYearError } = await supabase
      .from('metric_periods')
      .select('*')
      .eq('year_number', currentPeriod.year_number + 1)
      .eq('month_number', 1)
      .maybeSingle()
    
    if (nextYearError) {
      console.error('Error fetching next period:', nextYearError)
      return null
    }
    
    return nextYearData
  } catch (err) {
    console.error('Error in getNextPeriod:', err)
    return null
  }
}

/**
 * Форматировать период для отображения
 * Пример: "Январь 2026 - 4 недели (01.01 - 28.01)"
 */
export function formatPeriod(period: {
  period_start: string
  period_end: string
  month_number: number
  year_number: number
  period_len_days?: number
}): string {
  const start = new Date(period.period_start)
  const end = new Date(period.period_end)
  
  // Названия месяцев на русском
  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ]
  
  const monthName = monthNames[period.month_number - 1] || `Месяц ${period.month_number}`
  
  // Форматируем даты как ДД.ММ (с годом если период переходит через год)
  const startDay = start.getDate().toString().padStart(2, '0')
  const startMonth = (start.getMonth() + 1).toString().padStart(2, '0')
  const startYear = start.getFullYear()
  const endDay = end.getDate().toString().padStart(2, '0')
  const endMonth = (end.getMonth() + 1).toString().padStart(2, '0')
  const endYear = end.getFullYear()
  
  // Показываем год в названии месяца и в датах (если года разные)
  const dateRange = startYear !== endYear 
    ? `${startDay}.${startMonth}.${startYear} - ${endDay}.${endMonth}.${endYear}`
    : `${startDay}.${startMonth} - ${endDay}.${endMonth}`
  
  return `${monthName} ${period.year_number} (${dateRange})`
}

/**
 * Получить короткое название периода
 * Пример: "Месяц 1, 2026"
 */
export function formatPeriodShort(period: {
  month_number: number
  year_number: number
}): string {
  return `Месяц ${period.month_number}, ${period.year_number}`
}

/**
 * Загрузить все доступные периоды
 */
export async function getAllPeriods(supabase: any): Promise<MetricPeriod[]> {
  try {
    const { data, error } = await supabase
      .from('metric_periods')
      .select('*')
      .order('period_start', { ascending: true })
    
    if (error) {
      console.error('Error fetching all periods:', error)
      return []
    }
    
    return data || []
  } catch (err) {
    console.error('Error in getAllPeriods:', err)
    return []
  }
}
